import React, { useState, useEffect, useMemo, useRef } from "react";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";
import Icon from "../../../components/Icon";
import { TogglePill } from "../../../components/ui";
import { categoryLabel, buildCategoryStrip } from "./iconForTemplate";

// CMD label hint for the search pill — matches LibrarySearchToolbar.
// Detect-on-mount avoids SSR mismatch; falls back to ⌘ when unknown.
const CMD_LABEL = (() => {
  if (typeof window === "undefined") return "⌘";
  const ua = window.navigator?.userAgent || "";
  if (/Mac|iPhone|iPad|iPod/.test(ua)) return "⌘ K";
  return "Ctrl K";
})();

// agent-experience-redesign-v1.md §20.4 / M4.
//
// Marketplace search + category chips + sort. Mirrors LibrarySearchBar
// but the chip groups are different (no status/kind — templates
// don't have those; categories are dominant axis).
//
// Per §20.12 Q2: chip strip = union of (12 default categories +
// non-default categories actually in use). buildCategoryStrip
// enforces canonical order.
//
// Search is debounced 200ms — same as LibrarySearchBar.

const SORT_OPTIONS = [
  { value: "popular",            label: "Popular"            },
  { value: "newest",             label: "Newest"             },
  { value: "highest_rated",      label: "Highest rated"      },
  // i2-visual-identity-v2 §10.1 A14 — explicit "Recently published"
  // option (separate from "newest", which sorts by install count
  // recency in some catalog views).
  { value: "recently_published", label: "Recently published" },
];

const SORT_STORE_KEY = "marketplace.sort";

export function readMarketplaceSort() {
  try { return localStorage.getItem(SORT_STORE_KEY) || "popular"; }
  catch { return "popular"; }
}

export function writeMarketplaceSort(s) {
  try { localStorage.setItem(SORT_STORE_KEY, s); } catch {}
}

export default function MarketplaceSearchBar({
  value, onChange, templates = [], trailing, onOpenCommandPalette,
}) {
  const chips = useMemo(() => buildCategoryStrip(templates), [templates]);
  const counts = useMemo(() => {
    const c = {};
    for (const t of (templates || [])) {
      const k = t.category || "uncategorized";
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [templates]);

  // Glass pill base style — copied verbatim from LibrarySearchToolbar
  // so the search/sort/filters buttons render identically on both
  // pages. Same height, same radius, same hover treatment.
  const pillBase = {
    display: "flex", alignItems: "center", gap: 8,
    padding: "9px 14px", fontSize: 12, fontWeight: 500,
    color: COLORS.text,
    ...MATERIALS.thinGlass(),
    borderRadius: 12,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "transform 0.12s, box-shadow 0.12s, border-color 0.12s",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Single wrap-flex row — chips on the left as the leading slot,
          controls auto-margin to the right. Mirrors
          LibrarySearchToolbar's outer layout exactly so the two pages
          render identically: chips overflow to a "second row" via
          flex-wrap when the chip strip exceeds the viewport width. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        {/* Leading slot — category chips with TogglePill (identical
            component used by Library's CategoryStrip). The "All" pill
            is pinned first and counts the visible templates; per-
            category pills show their own counts. */}
        <div
          data-testid="marketplace-stats-strip"
          style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}
        >
          <TogglePill
            data-testid="marketplace-stats-chip-all"
            active={!value?.category}
            onClick={() => onChange({ ...value, category: "" })}
            count={templates.length}
          >
            All
          </TogglePill>
          {chips.map(c => (
            <TogglePill
              key={c}
              data-testid={`marketplace-stats-chip-${c}`}
              active={value?.category === c}
              onClick={() => onChange({
                ...value,
                category: value?.category === c ? "" : c,
              })}
              title={c}
              count={counts[c]}
            >
              {categoryLabel(c)}
            </TogglePill>
          ))}
        </div>

        {/* Right-flush controls — search pill (opens cmdK palette),
            sort dropdown, trailing slot (filters popover, density
            toggle). Same shape, same pill styling as Library. */}
        <div style={{
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
          marginLeft: "auto",
        }}>
          {/* Search pill button — opens MarketplaceCommandPalette on
              click. Visible (not hidden behind hotkey alone) for
              discoverability. Echoes the active search query as the
              button label so the user can see what's filtered. */}
          <button
            type="button"
            data-testid="marketplace-search-pill"
            onClick={() => onOpenCommandPalette && onOpenCommandPalette()}
            style={{
              ...pillBase,
              minWidth: 200, maxWidth: 280,
              color: COLORS.textMuted,
              textAlign: "left",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = `0 6px 20px ${COLORS.accent}26`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
            title="Search templates (⌘K)"
          >
            <Icon name="action.search" size={14} color={COLORS.textDim} />
            <span style={{ flex: 1 }}>
              {value?.search ? value.search : "Search templates"}
            </span>
            <kbd style={{
              padding: "2px 7px", fontSize: 10, fontWeight: 600,
              color: COLORS.textDim,
              background: `${COLORS.border}40`,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
            }}>{CMD_LABEL}</kbd>
          </button>

          {/* Sort — chevron rendered as an absolutely-positioned <span>
              child of a wrapper <div>. See LibrarySearchToolbar.jsx for
              the full why: pillBase's `background: rgba(...)` shorthand
              overrides our `backgroundRepeat: no-repeat` in some
              browsers, causing the chevron to tile across the pill. */}
          <div style={{ position: "relative", display: "inline-block" }}>
            <select
              data-testid="marketplace-sort"
              value={value?.sort || "popular"}
              onChange={(e) => {
                writeMarketplaceSort(e.target.value);
                onChange({ ...value, sort: e.target.value });
              }}
              style={{
                ...pillBase,
                fontWeight: 600,
                outline: "none",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                paddingRight: 28,
              }}
              title="Sort templates"
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>Sort: {o.label}</option>
              ))}
            </select>
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                width: 10,
                height: 10,
                pointerEvents: "none",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                   stroke={COLORS.textDim} strokeWidth="2.5"
                   strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </div>

          {/* Trailing slot — filters popover + density toggle from
              MarketplaceView. Same flex group as Library's
              toolbarTrailing. */}
          {trailing && (
            <div style={{ display: "flex", gap: 6 }}>
              {trailing}
            </div>
          )}
        </div>
      </div>

      {/* Active-filter chip strip + Clear all — i2-visual-identity-v2 §10.1 A11.
          Renders below the toolbar when any filter is applied. Each chip
          is removable via ✕; "Clear all" wipes the whole filter set. */}
      {(value?.search || value?.category) && (
        <div
          data-testid="marketplace-active-filters"
          style={{
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
            padding: "8px 12px", borderRadius: 10,
            background: COLORS.surface, border: `1px solid ${COLORS.border}`,
          }}
        >
          <span style={{
            fontSize: 10, fontWeight: 700, color: COLORS.textDim,
            textTransform: "uppercase", letterSpacing: "0.06em",
          }}>Active</span>
          {value.search && (
            <span
              data-testid="active-filter-search"
              style={chipStyle(true)}
            >
              Search: {value.search}
              <button
                data-testid="filter-chip-dismiss"
                onClick={() => onChange({ ...value, search: "" })}
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  marginLeft: 6, padding: 0, color: "inherit", fontSize: 11,
                }}
              >×</button>
            </span>
          )}
          {value.category && (
            <span
              data-testid="active-filter-category"
              style={chipStyle(true)}
            >
              {categoryLabel(value.category)}
              <button
                data-testid="filter-chip-dismiss"
                onClick={() => onChange({ ...value, category: "" })}
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  marginLeft: 6, padding: 0, color: "inherit", fontSize: 11,
                }}
              >×</button>
            </span>
          )}
          <button
            data-testid="marketplace-clear-filters"
            type="button"
            onClick={() => onChange({ ...value, search: "", category: "" })}
            style={{
              marginLeft: "auto",
              padding: "3px 10px", fontSize: 11, fontWeight: 600,
              background: "transparent", border: "none", cursor: "pointer",
              color: COLORS.textDim, letterSpacing: "0.02em",
            }}
          >Clear all</button>
        </div>
      )}
    </div>
  );
}

function chipStyle(active) {
  return {
    padding: "3px 10px", fontSize: 11, fontWeight: 600,
    borderRadius: 999, cursor: "pointer",
    background: active ? `${COLORS.accent}18` : "transparent",
    border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
    color: active ? COLORS.accent : COLORS.textMuted,
    transition: "all 0.15s",
    whiteSpace: "nowrap",
  };
}

// Helper: client-side filter+sort applied to a template list.
export function applyTemplateFilter(templates, sb) {
  let out = templates;
  if (sb?.category) {
    out = out.filter(t => t.category === sb.category);
  }
  if (sb?.search) {
    const q = sb.search.toLowerCase();
    out = out.filter(t =>
      (t.name || "").toLowerCase().includes(q) ||
      (t.description || "").toLowerCase().includes(q) ||
      (t.template || "").toLowerCase().includes(q),
    );
  }
  const sorted = [...out];
  if (sb?.sort === "newest") {
    sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  } else if (sb?.sort === "highest_rated") {
    sorted.sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0));
  } else if (sb?.sort === "recently_published") {
    // i2-visual-identity-v2 §10.1 A14 — published_at falls back to
    // created_at if the publish timestamp isn't surfaced separately.
    sorted.sort((a, b) =>
      (b.published_at || b.created_at || "").localeCompare(a.published_at || a.created_at || ""),
    );
  } else {
    // popular = install_count desc
    sorted.sort((a, b) => (b.install_count || 0) - (a.install_count || 0));
  }
  return sorted;
}
